import { ArrowLeft, ArrowRight, BadgeCheck, BookOpen, Bookmark, CalendarPlus, CheckCircle2, ChevronDown, GraduationCap, Mail, MapPin, Menu, Phone, Play, Send, Sparkles, Users, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { deleteUser, getAdditionalUserInfo, inMemoryPersistence, RecaptchaVerifier, setPersistence, signInWithPhoneNumber, updateProfile } from 'firebase/auth';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { createCourseSignup, getPublicAcademyBySlug, getPublicCourses, getPublicIntakes, getStudentCourseSignups, upsertUserDocument } from '../../firebase/firestore';
import { auth, embeddedAuth } from '../../firebase/firebase';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { createDefaultIntakeSession, formatIntakeSessionLabel, formatIntakeSessionsSummary, normalizeIntakeSessions } from '../../utils/intakeSchedule';
import { applyPageMetadata } from '../../utils/pageMetadata';
import { normalizePhoneNumber, isEmail } from '../../utils/validators';
import { applyThemeToDocument } from '../../utils/theme';
import { getPublicWebsiteContent } from '../../utils/publicWebsite';
import { phoneCountries } from '../../data/phoneCountries';
import { countryCurrencies } from '../../data/countryCurrencies';
import academyHeroImage from '../../assets/public-academy-hero.png';
import logoDark from '../../assets/logo-simple-dark.svg';
import { sendApplicationCommunications } from '../../services/communicationService';

const GENDER_OPTIONS = ['Female', 'Male', 'Prefer not to say'];
function getPreferredModeOptions(course) {
  switch (course?.courseType) {
    case 'live_online':
      return ['Live online classes'];
    case 'in_person':
      return ['In-person classes'];
    case 'hybrid':
      return ['Hybrid: in-person + online'];
    case 'self_paced':
      return ['Self-paced learning'];
    default:
      return ['Online classes', 'In-person classes', 'Hybrid learning'];
  }
}

function splitFullName(fullName = '') {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function createInitialSignupValues(courseId = '', intakeId = '', intakeSessionLabel = '') {
  return {
    courseId,
    intakeId,
    intakeSessionLabel,
    fullName: '',
    gender: '',
    countryOfResidence: 'Kenya',
    phone: '',
    phoneCountry: 'Kenya',
    phoneCountryCode: 'KE',
    countryDialCode: '254',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Nairobi',
    yearOfBirth: '',
    studyMode: 'Online classes',
    email: '',
    password: '',
  };
}

export function PublicAcademyPage({ forcedSlug = '' } = {}) {
  const { slug } = useParams();
  const { logout } = useAuth();
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [tenant, setTenant] = useState(null);
  const [courses, setCourses] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [values, setValues] = useState(createInitialSignupValues());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginValues, setLoginValues] = useState({ phone: '', phoneCountry: 'Kenya', phoneCountryCode: 'KE', countryDialCode: '254' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginOtpStep, setLoginOtpStep] = useState(false);
  const [loginOtp, setLoginOtp] = useState('');
  const [loginConfirmationResult, setLoginConfirmationResult] = useState(null);
  const loginRecaptchaVerifierRef = useRef(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [otpNotice, setOtpNotice] = useState('');
  const recaptchaVerifierRef = useRef(null);
  const [detectedCountryCode, setDetectedCountryCode] = useState('KE');
  const hasSelectedCountry = useRef(false);
  const academySlug = forcedSlug || slug || '';
  const isEmbedMode = searchParams.get('embed') === 'signup';
  const embedPrimaryColor = /^#[0-9a-f]{6}$/i.test(searchParams.get('primaryColor') || '')
    ? searchParams.get('primaryColor')
    : '#ef6a48';

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      const academy = await getPublicAcademyBySlug(academySlug);
      if (!academy) {
        if (mounted) {
          setTenant(null);
          setLoading(false);
        }
        return;
      }
      const [publicCourses, publicIntakes] = await Promise.all([getPublicCourses(academy.id), getPublicIntakes(academy.id)]);
      if (mounted) {
        setTenant(academy);
        setCourses(publicCourses);
        setIntakes(publicIntakes);
        setLoading(false);
      }
    }
    load().catch((err) => {
      console.error(err);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [academySlug]);

  useEffect(() => {
    let cancelled = false;
    fetch('https://us-central1-smart-campus-africa.cloudfunctions.net/detectVisitorCountry')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Country detection failed'))))
      .then(({ countryCode }) => {
        if (cancelled || hasSelectedCountry.current || !countryCode) return;
        const detectedCountry = phoneCountries.find(([code]) => code === countryCode);
        if (!detectedCountry) return;
        const [phoneCountryCode, phoneCountry, countryDialCode] = detectedCountry;
        setDetectedCountryCode(countryCode);
        setValues((current) => ({ ...current, countryOfResidence: phoneCountry, phoneCountry, phoneCountryCode, countryDialCode }));
      })
      .catch(() => {
        // Kenya remains the safe default when detection is unavailable.
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isEmbedMode || !courses.length || values.courseId) return;
    const firstCourseId = courses[0].id;
    const firstCourse = courses.find((course) => course.id === firstCourseId);
    const firstIntake = intakes.find((intake) => intake.courseId === firstCourseId);
    const firstSessions = normalizeIntakeSessions(firstIntake?.sessions);
    const selection = {
      courseId: firstCourseId,
      intakeId: firstIntake?.id || 'open',
      intakeSessionLabel: firstSessions[0] ? formatIntakeSessionLabel(firstSessions[0]) : 'Open session',
      studyMode: getPreferredModeOptions(firstCourse)[0],
    };
    setValues((current) => ({ ...current, ...selection }));
  }, [courses, intakes, isEmbedMode, values.courseId]);

  const activeCourse = useMemo(() => courses.find((course) => course.id === values.courseId) || null, [courses, values.courseId]);
  const courseIntakes = useMemo(() => intakes.filter((intake) => intake.courseId === values.courseId), [intakes, values.courseId]);
  const activeIntake = useMemo(() => courseIntakes.find((intake) => intake.id === values.intakeId) || null, [courseIntakes, values.intakeId]);
  const intakeSessions = useMemo(() => normalizeIntakeSessions(activeIntake?.sessions), [activeIntake]);
  const currency = tenant?.settings?.currency || 'KES';
  const academyTypeLabel = tenant?.type?.replace(/_/g, ' ') || 'Academy';
  const websiteContent = getPublicWebsiteContent(tenant?.settings?.publicWebsite);
  const heroTitle = websiteContent.heroTitle || tenant?.name || 'Your academy';
  const heroDescription = websiteContent.heroDescription || 'A polished front-facing academy site for applications, course discovery, and student login.';
  const heroImageSource = websiteContent.heroImageURL || academyHeroImage;
  const aboutTitle = websiteContent.aboutTitle || 'About us';
  const aboutDescription = websiteContent.aboutDescription || `${tenant?.name || 'This academy'} can present its story, public courses, and admissions flow in one clear place without making students hunt for the next step.`;
  const featuredCourse = courses[0] || null;
  const logoScale = Math.min(1.25, Math.max(0.75, Number(tenant?.branding?.logoScale) || 1));
  const aboutImageSource = websiteContent.aboutImageURL || heroImageSource;
  const whyImageSource = websiteContent.whyImageURL || heroImageSource;
  const whyImageSource2 = websiteContent.whyImageURL2 || heroImageSource;
  const modernHero = websiteContent.modernHero;
  const modernNavigation = websiteContent.modernNavigation;
  const modernAbout = websiteContent.modernAbout;
  const modernAboutImageSource = modernAbout.imageURL || aboutImageSource;
  const modernCourses = websiteContent.modernCourses;
  const modernContact = websiteContent.modernContact;
  const minimalNavigation = websiteContent.minimalNavigation;
  const minimalHero = websiteContent.minimalHero;
  const minimalAbout = websiteContent.minimalAbout;
  const minimalAboutImageSource = minimalAbout.imageURL || aboutImageSource;
  const minimalBenefits = websiteContent.minimalBenefits;
  const minimalCourses = websiteContent.minimalCourses;
  const minimalContact = websiteContent.minimalContact;
  const classicHero = websiteContent.classicHero;
  const minimalHeroImageSource = minimalHero.imageURL || heroImageSource;
  const minimalHeroImageSource2 = minimalHero.imageURL2 || minimalHeroImageSource;
  const minimalHeroImageSource3 = minimalHero.imageURL3 || minimalHeroImageSource;
  const classicHeroImageSource = classicHero.imageURL || heroImageSource;
  const classicHeroImageSource2 = classicHero.imageURL2 || classicHeroImageSource;
  const classicHeroImageSource3 = classicHero.imageURL3 || classicHeroImageSource;
  const classicHeroImageSource4 = classicHero.imageURL4 || classicHeroImageSource;
  const classicAbout = websiteContent.classicAbout;
  const classicAboutImageSource = classicAbout.imageURL || aboutImageSource;
  const classicWhy = websiteContent.classicWhy;
  const classicWhyImageSource = classicWhy.imageURL || whyImageSource;
  const classicWhyImageSource2 = classicWhy.imageURL2 || whyImageSource2;
  const contactEmail = websiteContent.contactEmail || tenant?.settings?.contactEmail || 'Add in settings';
  const contactPhone = websiteContent.contactPhone || tenant?.settings?.contactPhone || 'Add in settings';
  const contactLocation = websiteContent.contactLocation || tenant?.settings?.location || 'Add in settings';
  const classicContact = websiteContent.classicContact;
  const footer = websiteContent.footer;
  const classicContactDetails = [
    { id: 'email', icon: Mail, label: classicContact.emailLabel, value: contactEmail, hint: classicContact.emailHint },
    { id: 'phone', icon: Phone, label: classicContact.phoneLabel, value: contactPhone, hint: classicContact.phoneHint },
    { id: 'location', icon: MapPin, label: classicContact.locationLabel, value: contactLocation, hint: classicContact.locationHint },
  ];
  const templateOverride = searchParams.get('template');
  const isPreviewMode = searchParams.get('preview') === '1';
  const isAcademyLoginPage = window.location.pathname === '/login';
  const templateVariant = useMemo(() => {
    const candidate = String(templateOverride || '').trim().toLowerCase();
    if (['classic', 'modern', 'minimal', 'portal'].includes(candidate)) return candidate;
    return tenant?.settings?.publicTemplate || 'modern';
  }, [templateOverride, tenant?.settings?.publicTemplate]);
  const templateTitle = useMemo(() => {
    if (templateVariant === 'classic') return 'Classic Academic';
    if (templateVariant === 'minimal') return 'Minimal Elegant';
    if (templateVariant === 'portal') return 'Learning Portal';
    return 'Modern Admissions';
  }, [templateVariant]);
  const headerHeroPrimaryAction = templateVariant === 'classic' ? classicHero.primaryAction : websiteContent.heroPrimaryAction;
  const headerHeroSecondaryAction = templateVariant === 'classic' ? classicHero.secondaryAction : websiteContent.heroSecondaryAction;
  const platformLoginOrigin = useMemo(() => {
    // Keep staff and students on the academy host they used to sign in.
    // This preserves tenant context for both local and production subdomains.
    return window.location.origin;
  }, []);

  async function onPortalLogin(event) {
    event.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const normalizedPhone = normalizePhoneNumber(loginValues.phone, loginValues.countryDialCode);
      if (!normalizedPhone || normalizedPhone.length < 8) {
        setLoginError('Enter a valid phone number.');
        return;
      }

      if (!loginConfirmationResult) {
        if (!loginRecaptchaVerifierRef.current) {
          loginRecaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'academy-login-recaptcha', {
            size: 'invisible',
            'expired-callback': () => { loginRecaptchaVerifierRef.current = null; },
          });
        }
        const pendingConfirmation = await signInWithPhoneNumber(auth, normalizedPhone, loginRecaptchaVerifierRef.current);
        setLoginConfirmationResult(pendingConfirmation);
        setLoginOtpStep(true);
        setLoginOtp('');
        setLoginLoading(false);
        return;
      }

      const credential = await loginConfirmationResult.confirm(loginOtp.trim());
      const studentSignups = await getStudentCourseSignups(credential.user.uid);
      const academySignup = studentSignups.find((signup) => signup.tenantId === tenant.id);
      if (!academySignup) {
        if (getAdditionalUserInfo(credential)?.isNewUser) {
          try { await deleteUser(credential.user); } catch (cleanupError) { console.error('Unable to remove unconnected phone account', cleanupError); }
        }
        await logout();
        setLoginConfirmationResult(null);
        setLoginOtpStep(false);
        setLoginOtp('');
        setLoginError('This phone number is not connected to this academy. Sign up for a course to create access.');
        return;
      }

      window.location.assign(`${platformLoginOrigin}/student/portal`);
    } catch (error) {
      console.error(error);
      setLoginError(error.message || 'Unable to sign in. Check your email and password.');
    } finally {
      setLoginLoading(false);
    }
  }

  const totalSessionBlocks = useMemo(() => intakes.reduce((total, intake) => total + normalizeIntakeSessions(intake.sessions).length, 0), [intakes]);
  const academyStats = useMemo(() => ([
    { label: 'Courses', value: String(courses.length), hint: 'Publicly visible' },
    { label: 'Intakes', value: String(intakes.length), hint: 'Open and cohort' },
    { label: 'Session blocks', value: String(totalSessionBlocks), hint: 'Flexible schedules' },
  ]), [courses.length, intakes.length, totalSessionBlocks]);
  const modernAcademyStats = useMemo(() => ([
    { label: modernHero.stat1Label, value: String(courses.length), hint: modernHero.stat1Hint },
    { label: modernHero.stat2Label, value: String(intakes.length), hint: modernHero.stat2Hint },
    { label: modernHero.stat3Label, value: String(totalSessionBlocks), hint: modernHero.stat3Hint },
  ]), [courses.length, intakes.length, modernHero.stat1Hint, modernHero.stat1Label, modernHero.stat2Hint, modernHero.stat2Label, modernHero.stat3Hint, modernHero.stat3Label, totalSessionBlocks]);
  const minimalAcademyStats = useMemo(() => ([
    { label: minimalHero.stat1Label, value: String(courses.length), hint: minimalHero.stat1Hint },
    { label: minimalHero.stat2Label, value: String(intakes.length), hint: minimalHero.stat2Hint },
    { label: minimalHero.stat3Label, value: String(totalSessionBlocks), hint: minimalHero.stat3Hint },
  ]), [courses.length, intakes.length, minimalHero.stat1Hint, minimalHero.stat1Label, minimalHero.stat2Hint, minimalHero.stat2Label, minimalHero.stat3Hint, minimalHero.stat3Label, totalSessionBlocks]);
  const contactDetails = useMemo(() => ([
    {
      icon: Mail,
      label: 'Admissions email',
      value: contactEmail,
      hint: 'Shown to prospective students.',
    },
    {
      icon: Phone,
      label: 'Phone number',
      value: contactPhone,
      hint: 'Good for fast follow-up.',
    },
    {
      icon: MapPin,
      label: 'Location',
      value: contactLocation,
      hint: 'Campus or virtual office.',
    },
  ]), [contactEmail, contactLocation, contactPhone]);
  const modernContactDetails = useMemo(() => ([
    { icon: Mail, label: modernContact.emailLabel, value: contactEmail, hint: modernContact.emailHint },
    { icon: Phone, label: modernContact.phoneLabel, value: contactPhone, hint: modernContact.phoneHint },
    { icon: MapPin, label: modernContact.locationLabel, value: contactLocation, hint: modernContact.locationHint },
  ]), [contactEmail, contactLocation, contactPhone, modernContact.emailHint, modernContact.emailLabel, modernContact.locationHint, modernContact.locationLabel, modernContact.phoneHint, modernContact.phoneLabel]);
  const minimalContactDetails = useMemo(() => ([
    { icon: Mail, label: minimalContact.emailLabel, value: contactEmail, hint: minimalContact.emailHint },
    { icon: Phone, label: minimalContact.phoneLabel, value: contactPhone, hint: minimalContact.phoneHint },
    { icon: MapPin, label: minimalContact.locationLabel, value: contactLocation, hint: minimalContact.locationHint },
  ]), [contactEmail, contactLocation, contactPhone, minimalContact.emailHint, minimalContact.emailLabel, minimalContact.locationHint, minimalContact.locationLabel, minimalContact.phoneHint, minimalContact.phoneLabel]);
  const orderedPhoneCountries = [
    ...phoneCountries.filter(([code]) => code === detectedCountryCode),
    ...phoneCountries.filter(([code]) => code !== detectedCountryCode),
  ];

  function selectCountry(event) {
    const [phoneCountryCode, phoneCountry, countryDialCode] = event.target.value.split('|');
    hasSelectedCountry.current = true;
    setValues((current) => ({ ...current, countryOfResidence: phoneCountry, phoneCountry, phoneCountryCode, countryDialCode }));
  }

  useEffect(() => {
    if (!tenant) {
      document.title = academySlug || 'Smart Campus Africa';
      applyThemeToDocument(null);
      return undefined;
    }

    applyThemeToDocument(tenant?.branding?.primaryColor || null);

    return applyPageMetadata({
      title: tenant.name,
      description: heroDescription,
      canonicalUrl: window.location.href,
      ogTitle: tenant.name,
      ogDescription: heroDescription,
    });
  }, [academySlug, heroDescription, tenant]);

  function scrollToSection(sectionId) {
    setMobileMenuOpen(false);
    const section = document.getElementById(sectionId);
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleHeroApply() {
    if (featuredCourse) {
      openSignup(featuredCourse);
      return;
    }
    scrollToSection('courses');
  }

  function createSelectionDefaults(courseId) {
    const availableIntakes = intakes.filter((intake) => intake.courseId === courseId);
    const intake = availableIntakes[0] || null;
    const sessions = normalizeIntakeSessions(intake?.sessions);
    return {
      courseId,
      intakeId: intake?.id || 'open',
      intakeSessionLabel: sessions[0] ? formatIntakeSessionLabel(sessions[0]) : 'Open session',
      studyMode: getPreferredModeOptions(courses.find((course) => course.id === courseId))[0],
    };
  }

  function openSignup(course) {
    setSelectedCourseId(course.id);
    setSubmitted(false);
    setError('');
    setConfirmationResult(null);
    setOtp('');
    setOtpStep(false);
    setOtpNotice('');
    const selection = createSelectionDefaults(course.id);
    const detectedCountry = phoneCountries.find(([code]) => code === detectedCountryCode) || phoneCountries.find(([code]) => code === 'KE');
    const [phoneCountryCode, phoneCountry, countryDialCode] = detectedCountry;
    setValues({
      ...createInitialSignupValues(selection.courseId, selection.intakeId, selection.intakeSessionLabel),
      studyMode: selection.studyMode,
      countryOfResidence: phoneCountry,
      phoneCountry,
      phoneCountryCode,
      countryDialCode,
    });
  }

  function closeSignup() {
    setSelectedCourseId('');
    setSubmitted(false);
    setError('');
    setConfirmationResult(null);
    setOtp('');
    setOtpStep(false);
    setOtpNotice('');
    setValues(createInitialSignupValues());
  }

  function clearRecaptcha() {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
  }

  async function sendPhoneOtp(phoneNumber) {
    const phoneAuth = isEmbedMode ? embeddedAuth : auth;
    if (isEmbedMode) await setPersistence(embeddedAuth, inMemoryPersistence);
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(phoneAuth, 'academy-phone-recaptcha', {
        size: 'invisible',
        'expired-callback': () => { recaptchaVerifierRef.current = null; },
      });
    }
    const pendingConfirmation = await signInWithPhoneNumber(phoneAuth, phoneNumber, recaptchaVerifierRef.current);
    setConfirmationResult(pendingConfirmation);
    setOtpStep(true);
    setOtp('');
    setOtpNotice('A new verification code has been sent.');
  }

  function returnToApplicationDetails() {
    clearRecaptcha();
    setConfirmationResult(null);
    setOtpStep(false);
    setOtp('');
    setOtpNotice('');
    setError('');
  }

  async function resendPhoneOtp() {
    const cleanedPhone = normalizePhoneNumber(values.phone, values.countryDialCode);
    setError('');
    setSaving(true);
    try {
      clearRecaptcha();
      await sendPhoneOtp(cleanedPhone);
    } catch (err) {
      setError(err.message || 'Unable to resend the verification code.');
    } finally {
      setSaving(false);
    }
  }

  async function onSubmit(event) {
    event.preventDefault();
    setError('');

    const cleanedPhone = normalizePhoneNumber(values.phone, values.countryDialCode);
    if (!isEmail(values.email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (!cleanedPhone || cleanedPhone.length < 8) {
      setError('Enter a valid phone number.');
      return;
    }

    const selectedCourse = courses.find((course) => course.id === values.courseId);
    if (!selectedCourse) {
      setError('Choose a course to continue.');
      return;
    }

    setSaving(true);
    let createdUser = null;
    let newlyCreatedUser = false;
    try {
      if (!confirmationResult) {
        await sendPhoneOtp(cleanedPhone);
        setSaving(false);
        return;
      }

      const credential = await confirmationResult.confirm(otp.trim());
      createdUser = credential.user;
      newlyCreatedUser = Boolean(getAdditionalUserInfo(credential)?.isNewUser);
      await updateProfile(createdUser, { displayName: values.fullName });
      if (!isEmbedMode) {
        await upsertUserDocument(createdUser, {
          displayName: values.fullName,
          email: values.email,
          phone: cleanedPhone,
          country: values.countryOfResidence || values.phoneCountry,
          countryCode: values.phoneCountryCode,
          phoneCountry: values.phoneCountry,
          phoneCountryCode: values.phoneCountryCode,
          phoneCountryDialCode: values.countryDialCode,
          timezone: values.timezone,
          currency: countryCurrencies[values.phoneCountryCode] || 'USD',
        });
      }

      const selectedIntake = intakes.find((intake) => intake.id === values.intakeId);
      const intakeSessionsForSelection = normalizeIntakeSessions(selectedIntake?.sessions);
      const selectedSession = intakeSessionsForSelection.find((session) => formatIntakeSessionLabel(session) === values.intakeSessionLabel) || intakeSessionsForSelection[0] || createDefaultIntakeSession();
      const { firstName, lastName } = splitFullName(values.fullName);

      await createCourseSignup(tenant.id, {
        fullName: values.fullName,
        firstName,
        lastName,
        courseId: selectedCourse.id,
        courseName: selectedCourse.name,
        intakeId: selectedIntake?.id || null,
        intakeLabel: selectedIntake?.name || 'Open intake',
        intakeSessionId: selectedSession.id || null,
        intakeSessionLabel: values.intakeSessionLabel || formatIntakeSessionLabel(selectedSession),
        gender: values.gender,
        countryOfResidence: values.countryOfResidence || values.phoneCountry,
        timezone: values.timezone,
        currency: countryCurrencies[values.phoneCountryCode] || 'USD',
        yearOfBirth: values.yearOfBirth,
        studyMode: values.studyMode,
        email: values.email,
        phone: cleanedPhone,
        authUid: createdUser.uid,
        authEmail: createdUser.email,
        authStatus: 'created',
      });

      try {
        await sendApplicationCommunications({
          user: createdUser,
          application: {
            fullName: values.fullName,
            email: values.email,
            phone: cleanedPhone,
            courseName: selectedCourse.name,
            duration: selectedCourse.duration,
            academyName: tenant.name,
          },
        });
      } catch (communicationError) {
        console.error('Application communications could not be sent', communicationError);
      }

      if (!isEmbedMode) {
        try {
          await logout();
        } catch (logoutError) {
          console.error('Unable to sign out after public signup', logoutError);
        }
      }
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      if (createdUser && newlyCreatedUser) {
        try {
          await deleteUser(createdUser);
        } catch (cleanupError) {
          console.error('Unable to clean up failed public signup account', cleanupError);
        }
      }
      setError(err.message || 'Unable to submit your signup. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingState className="academy-loading-state" label="Loading academy..." />;

  if (!tenant) {
    return (
      <main className="academy-public academy-missing-page">
        <header className="academy-missing-nav">
          <Link to="/" className="academy-missing-brand" aria-label="SmartCampus home">
            <span className="academy-missing-brand-mark"><img src={logoDark} alt="" aria-hidden="true" /></span>
            <span><strong>SmartCampus</strong><small>Education, connected</small></span>
          </Link>
          <Link to="/" className="academy-missing-back"><ArrowLeft size={16} /> Back to SmartCampus</Link>
        </header>
        <section className="academy-missing-hero">
          <div className="academy-missing-copy">
            <span className="academy-missing-code">404 <b>／</b> ACADEMY LINK</span>
            <h1>This academy link<br /><em>couldn&apos;t be found.</em></h1>
            <p>The address may be incorrect, or this academy may no longer be accepting visitors here. Check the link and try again, or return to SmartCampus to find your next step.</p>
            <div className="academy-missing-actions">
              <Link to="/" className="academy-missing-primary">Return to SmartCampus <ArrowRight size={17} /></Link>
              <Link to="/register" className="academy-missing-secondary">Create an academy</Link>
            </div>
          </div>
          <div className="academy-missing-art" aria-hidden="true">
            <img className="academy-missing-round-image" src={academyHeroImage} alt="" />
            <div className="academy-missing-orbit academy-missing-orbit--one" />
            <div className="academy-missing-orbit academy-missing-orbit--two" />
            <div className="academy-missing-card academy-missing-card--tag">LINK<br /><b>NOT FOUND</b></div>
          </div>
        </section>
      </main>
    );
  }

  if (templateVariant === 'portal' || isAcademyLoginPage) {
    return (
      <main className="academy-login-portal" style={{ '--academy-portal-accent': tenant?.branding?.primaryColor || '#f16b4f' }}>
        <div className="academy-login-portal-orbit academy-login-portal-orbit--one" />
        <div className="academy-login-portal-orbit academy-login-portal-orbit--two" />
        <section className="academy-login-portal-card">
          {tenant.branding?.logoURL ? <img className="academy-login-portal-logo" src={tenant.branding.logoURL} alt={`${tenant.name} logo`} style={{ transform: `scale(${logoScale})` }} /> : <div className="academy-login-portal-mark">{tenant.name.charAt(0).toUpperCase()}</div>}
          <h1>{loginOtpStep ? <>Enter the<br /><em>verification code.</em></> : <>Sign in to<br /><em>your account.</em></>}</h1>
          <p className="academy-login-portal-copy">{loginOtpStep ? `We sent a 6-digit OTP to +${loginValues.countryDialCode} ${loginValues.phone}.` : `Sign in to continue with ${tenant?.name || 'your academy'}.`}</p>
          <form className="academy-login-portal-form" onSubmit={onPortalLogin}>
            {loginError ? <div className="academy-login-portal-error" role="alert">{loginError}</div> : null}
            {!loginOtpStep ? <>
              <label htmlFor="academy-portal-phone">Phone number</label>
              <div className="academy-login-portal-input academy-login-phone-input">
                <Phone size={16} />
                <span className="academy-login-country-wrap"><select id="academy-login-country" aria-label="Phone country" value={`${loginValues.phoneCountryCode}|${loginValues.phoneCountry}|${loginValues.countryDialCode}`} onChange={(event) => { const [phoneCountryCode, phoneCountry, countryDialCode] = event.target.value.split('|'); setLoginValues((current) => ({ ...current, phoneCountryCode, phoneCountry, countryDialCode })); }}>{orderedPhoneCountries.map(([code, name, dialCode]) => <option key={`${code}-${dialCode}`} value={`${code}|${name}|${dialCode}`}>{name} (+{dialCode})</option>)}</select><ChevronDown size={13} /></span>
                <input id="academy-portal-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="712 345 678" required value={loginValues.phone} onChange={(event) => setLoginValues({ ...loginValues, phone: event.target.value })} />
              </div>
              <small className="academy-login-otp-note">You&apos;ll receive an OTP to verify your phone number.</small>
              <div id="academy-login-recaptcha" />
            </> : (
              <>
                <label htmlFor="academy-login-otp">Verification code</label>
                <div className="academy-login-portal-input"><Mail size={16} /><input id="academy-login-otp" type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="Enter the 6-digit OTP" required autoFocus value={loginOtp} onChange={(event) => setLoginOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} /></div>
                <button className="academy-login-otp-resend" type="button" onClick={() => { setLoginConfirmationResult(null); setLoginOtpStep(false); setLoginOtp(''); setLoginError(''); }}>Change phone number</button>
              </>
            )}
            <button className="academy-login-portal-submit" type="submit" disabled={loginLoading || (loginOtpStep && loginOtp.length < 6)}>{loginLoading ? (loginOtpStep ? 'Verifying...' : 'Sending code...') : (loginOtpStep ? <>Verify and sign in <ArrowRight size={16} /></> : <>Send sign-in code <ArrowRight size={16} /></>)}</button>
          </form>
        </section>
      </main>
    );
  }

  const sessionOptions = intakeSessions.length ? intakeSessions : [];

  if (isEmbedMode) {
    return (
      <main className="academy-signup-embed" style={{ '--academy-embed-accent': embedPrimaryColor }}>
        {submitted ? (
          <div className="academy-signup-embed-success">
            <div><BadgeCheck size={22} /></div>
            <h1>Application received</h1>
            <p>Thanks, {values.fullName}. {tenant.name} can now review your application.</p>
          </div>
        ) : (
          <form className="academy-signup-embed-form" onSubmit={onSubmit}>
            {error ? <div className="form-error">{error}</div> : null}
            <FormField label="Full Name" id="embed-full-name"><input id="embed-full-name" required value={values.fullName} onChange={(event) => setValues({ ...values, fullName: event.target.value })} placeholder="Jane Doe" /></FormField>
            <div className="form-grid">
              <FormField label="Gender" id="embed-gender"><select id="embed-gender" value={values.gender} onChange={(event) => setValues({ ...values, gender: event.target.value })}><option value="">Select gender</option>{GENDER_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></FormField>
              <FormField label="Year of Birth" id="embed-year-of-birth"><input id="embed-year-of-birth" type="number" min="1900" max={new Date().getFullYear()} value={values.yearOfBirth} onChange={(event) => setValues({ ...values, yearOfBirth: event.target.value })} placeholder="2002" /></FormField>
            </div>
            <div className="form-grid">
              <FormField label="Country of Residence" id="embed-country"><div className="academy-country-select"><select id="embed-country" required value={`${values.phoneCountryCode}|${values.phoneCountry}|${values.countryDialCode}`} onChange={selectCountry}>{orderedPhoneCountries.map(([code, name, dialCode]) => <option key={`${code}-${dialCode}`} value={`${code}|${name}|${dialCode}`}>{name}</option>)}</select><ChevronDown size={14} aria-hidden="true" /></div></FormField>
              <FormField label="Phone Number" id="embed-phone"><div className="academy-phone-field"><span>+{values.countryDialCode}</span><input id="embed-phone" required inputMode="tel" value={values.phone} onChange={(event) => setValues({ ...values, phone: event.target.value })} placeholder="712 345 678" /></div><small className="academy-phone-note">You&apos;ll receive an OTP to verify this phone number.</small></FormField>
            </div>
            <div id="academy-phone-recaptcha" />
            {otpStep ? <FormField label="Verification code" id="embed-otp"><input id="embed-otp" required inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter the 6-digit OTP" /><small className="academy-phone-note">Enter the code sent to +{values.countryDialCode} {values.phone}.</small></FormField> : null}
            <FormField label="Email" id="embed-email"><input id="embed-email" type="email" required value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} placeholder="you@example.com" /></FormField>
            <div className="form-grid">
              <FormField label="Course" id="embed-course"><select id="embed-course" value={values.courseId} onChange={(event) => { const selection = createSelectionDefaults(event.target.value); setValues((current) => ({ ...current, ...selection })); }}>{courses.length ? courses.map((course) => <option key={course.id} value={course.id}>{course.name}</option>) : <option value="">No published courses yet</option>}</select></FormField>
              <FormField label="Preferred Mode of Study" id="embed-study-mode"><select id="embed-study-mode" value={values.studyMode} onChange={(event) => setValues({ ...values, studyMode: event.target.value })}>{getPreferredModeOptions(activeCourse).map((option) => <option key={option} value={option}>{option}</option>)}</select><small>Based on the selected course.</small></FormField>
            </div>
            <FormField label="Intake / Cohort" id="embed-intake"><select id="embed-intake" value={values.intakeId} onChange={(event) => { const intakeId = event.target.value; const nextIntake = intakes.find((intake) => intake.id === intakeId); const nextSession = normalizeIntakeSessions(nextIntake?.sessions)[0]; setValues((current) => ({ ...current, intakeId, intakeSessionLabel: nextSession ? formatIntakeSessionLabel(nextSession) : 'Open session' })); }}>{courseIntakes.length ? courseIntakes.map((intake) => <option key={intake.id} value={intake.id}>{intake.name}</option>) : <option value="open">Open intake</option>}</select></FormField>
            <FormField label="Class Session Time" id="embed-session"><select id="embed-session" value={values.intakeSessionLabel} onChange={(event) => setValues({ ...values, intakeSessionLabel: event.target.value })}>{sessionOptions.length ? sessionOptions.map((session) => <option key={formatIntakeSessionLabel(session)} value={formatIntakeSessionLabel(session)}>{formatIntakeSessionLabel(session)}</option>) : <option value="Open session">Open session</option>}</select></FormField>
            <button type="submit" disabled={saving || !courses.length || (otpStep && otp.length < 6)} style={{ backgroundColor: embedPrimaryColor }}>{saving ? (otpStep ? 'Verifying phone...' : 'Sending OTP...') : (otpStep ? 'Verify phone & submit' : 'Continue with phone verification')} <ArrowRight size={15} /></button>
          </form>
        )}
      </main>
    );
  }

  const openingNote = featuredCourse
    ? `Featured course: ${featuredCourse.name}.`
    : 'Publish your first course to showcase it here.';

  function renderCourseCard(course) {
    const matchingIntakes = intakes.filter((intake) => intake.courseId === course.id);
    return (
      <article className="academy-template-courseCard academy-modern-course-card" key={course.id}>
        <div className="academy-modernCourse-image">
          <img src={course.imageURL || heroImageSource} alt={course.name ? `${course.name} course` : modernCourses.courseBadgeFallback} />
          <span>{course.code || modernCourses.courseBadgeFallback}</span>
        </div>
        <h3>{course.name}</h3>
        <p>{course.description || course.publicEnrollment?.signupMessage || modernCourses.descriptionFallback}</p>
        <div className="academy-course-meta">
          <strong>{course.duration || modernCourses.durationFallback}</strong>
          <strong>{formatCurrency(course.feeAmount || 0, currency)}</strong>
        </div>
        <div className="academy-course-intakes">
          {matchingIntakes.length ? matchingIntakes.map((intake) => (
            <small key={intake.id}>
              <CalendarPlus size={14} />
              {intake.name}
              {intake.startDate ? ` starts ${formatDate(intake.startDate)}` : ''}
              {intake.sessions?.length ? ` - ${formatIntakeSessionsSummary(intake.sessions)}` : ''}
            </small>
          )) : null}
        </div>
        <Button onClick={() => openSignup(course)} icon={Send}>{modernCourses.cardAction}</Button>
      </article>
    );
  }

  return (
    <main
      className={`academy-public academy-template academy-template--${templateVariant} academy-course-align--${(templateVariant === 'modern' ? modernCourses.alignment : templateVariant === 'minimal' ? minimalCourses.alignment : websiteContent.courseAlignment) || 'left'}`}
      style={{
        '--academy-site-page-bg': websiteContent.pageBackgroundColor,
        '--academy-site-surface': websiteContent.surfaceColor,
        '--academy-site-heading': websiteContent.headingColor,
        '--academy-site-body': websiteContent.bodyColor,
        '--academy-site-footer-bg': websiteContent.footerBackgroundColor,
        '--academy-site-footer-text': websiteContent.footerTextColor,
        '--academy-hero-position': websiteContent.heroImagePosition,
      }}
    >
      <header className="academy-template-nav">
        <button type="button" className="academy-template-brand" onClick={() => scrollToSection('home')}>
          {tenant.branding?.logoURL ? <span className="academy-template-brand-logo-slot"><img className="academy-template-brand-logo" src={tenant.branding.logoURL} alt={`${tenant.name} logo`} style={{ transform: `scale(${logoScale})` }} /></span> : <><div className="academy-template-mark"><span>{tenant.name.charAt(0).toUpperCase()}</span></div><span><strong>{tenant.name}</strong><small>{academyTypeLabel}</small></span></>}
        </button>

        <nav className="academy-template-links" aria-label="Academy sections">
          <a href="#home">{templateVariant === 'modern' ? modernNavigation.homeLabel : templateVariant === 'minimal' ? minimalNavigation.homeLabel : 'Home'}</a>
          <a href="#about">{templateVariant === 'modern' ? modernNavigation.aboutLabel : templateVariant === 'minimal' ? minimalNavigation.aboutLabel : 'About Us'}</a>
          <a href="#courses">{templateVariant === 'modern' ? modernNavigation.coursesLabel : templateVariant === 'minimal' ? minimalNavigation.coursesLabel : 'Courses'}</a>
          <a href="#contact">{templateVariant === 'modern' ? modernNavigation.contactLabel : templateVariant === 'minimal' ? minimalNavigation.contactLabel : 'Contact Us'}</a>
        </nav>

        <div className="academy-template-actions">
          <Button onClick={handleHeroApply} icon={Sparkles}>{headerHeroPrimaryAction}</Button>
          <Button to="/login" variant="secondary" icon={GraduationCap}>{headerHeroSecondaryAction}</Button>
        </div>
        <button
          type="button"
          className="academy-template-menuToggle"
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      {mobileMenuOpen ? (
        <div className="academy-template-mobileMenu">
          <nav aria-label="Mobile academy sections">
            <button type="button" onClick={() => scrollToSection('home')}>{templateVariant === 'modern' ? modernNavigation.homeLabel : templateVariant === 'minimal' ? minimalNavigation.homeLabel : 'Home'}</button>
            <button type="button" onClick={() => scrollToSection('about')}>{templateVariant === 'modern' ? modernNavigation.aboutLabel : templateVariant === 'minimal' ? minimalNavigation.aboutLabel : 'About us'}</button>
            {templateVariant === 'classic' ? <button type="button" onClick={() => scrollToSection('why-study')}>Why study with us</button> : null}
            <button type="button" onClick={() => scrollToSection('courses')}>{templateVariant === 'modern' ? modernNavigation.coursesLabel : templateVariant === 'minimal' ? minimalNavigation.coursesLabel : 'Courses'}</button>
            <button type="button" onClick={() => scrollToSection('contact')}>{templateVariant === 'modern' ? modernNavigation.contactLabel : templateVariant === 'minimal' ? minimalNavigation.contactLabel : 'Contact us'}</button>
          </nav>
          <div className="academy-template-mobileMenu-actions">
            <Button onClick={handleHeroApply}>{headerHeroPrimaryAction}</Button>
            <Button to="/login" variant="secondary" onClick={() => setMobileMenuOpen(false)}>{headerHeroSecondaryAction}</Button>
          </div>
        </div>
      ) : null}

      {templateVariant === 'classic' ? (
        <div className="academy-classic-template">
          <section
            id="home"
            className="academy-template-hero academy-template-hero--classic"
            style={{
              '--academy-hero-image': `url(${classicHeroImageSource})`,
              backgroundPosition: classicHero.imagePosition,
            }}
          >
            <div className="academy-classicHeroGrid">
              <div className="academy-classicHero-float academy-classicHero-float--one"><img src={classicHeroImageSource} alt="" style={{ objectPosition: classicHero.imagePosition }} /></div>
              <div className="academy-classicHero-float academy-classicHero-float--two"><img src={classicHeroImageSource2} alt="" style={{ objectPosition: classicHero.imagePosition }} /></div>
              <div className="academy-classicHero-float academy-classicHero-float--three"><img src={classicHeroImageSource3} alt="" style={{ objectPosition: classicHero.imagePosition }} /></div>
              <div className="academy-classicHero-float academy-classicHero-float--four"><img src={classicHeroImageSource4} alt="" style={{ objectPosition: classicHero.imagePosition }} /></div>
              <div className="academy-classicHeroCopy">
                <p className="academy-kicker">{classicHero.eyebrow}</p>
                {isPreviewMode ? <p className="academy-previewBadge">Template preview · {templateTitle}</p> : null}
                <h1><span>{classicHero.title || heroTitle}</span><em>{classicHero.accent}</em></h1>
                <p className="academy-heroCopy">{classicHero.description || heroDescription}</p>

                <div className="academy-template-heroActions">
                  <Button onClick={() => scrollToSection('courses')} icon={ArrowRight}>{classicHero.primaryAction}</Button>
                  <Button to="/login" variant="secondary" icon={GraduationCap}>{classicHero.secondaryAction}</Button>
                </div>

                <div className="academy-template-chipRow academy-template-chipRow--classic" aria-label="Academy summary">
                  {academyStats.map((item) => (
                    <div className="academy-template-chip" key={item.label}>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                      <small>{item.hint}</small>
                    </div>
                  ))}
                </div>
              </div>

              <aside className="academy-classicHeroPanel">
                <span>{classicHero.featuredLabel}</span>
                {featuredCourse ? (
                  <>
                    <strong>{featuredCourse.name}</strong>
                    <p>{featuredCourse.description || featuredCourse.publicEnrollment?.signupMessage || 'Signup is open for this course.'}</p>
                    <div className="academy-classicHeroPanelMeta">
                      <small>{featuredCourse.code}</small>
                      <small>{featuredCourse.duration || 'Flexible duration'}</small>
                      <small>{formatCurrency(featuredCourse.feeAmount || 0, currency)}</small>
                    </div>
                    <Button onClick={() => openSignup(featuredCourse)} icon={Send}>{classicHero.featuredAction}</Button>
                  </>
                ) : (
                  <EmptyState title="No featured course yet." message="Publish a course to highlight it here." icon={BookOpen} />
                )}
                <div className="academy-classicHeroPanelList">
                  {academyStats.map((item) => (
                    <div key={item.label}>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          </section>

          <section id="about" className="academy-template-section academy-classicSection">
            <div className="academy-classicAbout-reference">
              <div className="academy-classicAbout-reference-image">
                <img src={classicAboutImageSource} alt={`${tenant.name} learner studying`} />
                <div className="academy-classicAbout-rate"><small>{classicAbout.rateLabelLine1}<br />{classicAbout.rateLabelLine2}</small><span>{classicAbout.rateChange}</span><strong>{classicAbout.rateValue}</strong></div>
              </div>
              <div className="academy-classicAbout-reference-copy">
                <span className="academy-classicAbout-reference-kicker">{classicAbout.eyebrow}</span>
                <h2>{classicAbout.title || aboutTitle}</h2>
                <p>{classicAbout.description || aboutDescription}</p>
                <div className="academy-classicAbout-highlights">{classicAbout.highlights.map((item) => <div key={item}><BadgeCheck size={15} /><span>{item}</span></div>)}</div>
                <div className="academy-classicAbout-reference-stats">
                  <div><strong>{courses.length ? `${courses.length}+` : 'Ready'}</strong><span>{classicAbout.firstStatLine1}<br />{classicAbout.firstStatLine2}</span></div>
                  <div><strong>{intakes.length ? `${intakes.length}+` : 'One'}</strong><span>{classicAbout.secondStatLine1}<br />{classicAbout.secondStatLine2}</span></div>
                </div>
                <div className="academy-classicAbout-reference-actions">
                  <Button onClick={() => scrollToSection('courses')}>{classicHero.primaryAction}</Button>
                  <Button onClick={handleHeroApply} variant="secondary">{classicHero.secondaryAction}</Button>
                </div>
              </div>
            </div>
          </section>

          <section id="why-study" className="academy-template-section academy-classicWhyStudy">
            <div className="academy-classicWhyStudy-heading">
              <h2>{classicWhy.title}</h2>
            </div>
            <div className="academy-classicWhyStudy-grid">
              {[0, 1, 2, 3].map((index) => {
                const item = classicWhy.cards[index];
                const WhyIcon = [Sparkles, BookOpen, Users, BadgeCheck][index];
                const tone = ['orange', 'blue', 'cream', 'wine'][index];
                const card = <article className={`academy-classicWhyStudy-card academy-classicWhyStudy-card--${tone}`} style={{ backgroundColor: item.color, color: item.textColor }} key={`why-card-${index + 1}`}><WhyIcon size={18} aria-hidden="true" /><h3>{item.title}</h3><p>{item.description}</p></article>;
                if (index === 0) return <Fragment key={`why-fragment-${index + 1}`}>{card}</Fragment>;
                if (index === 1) return <Fragment key={`why-fragment-${index + 1}`}><div className="academy-classicWhyStudy-image academy-classicWhyStudy-image--top"><img src={classicWhyImageSource} alt={classicWhy.imageAlt} /></div>{card}</Fragment>;
                if (index === 3) return <Fragment key={`why-fragment-${index + 1}`}>{card}<div className="academy-classicWhyStudy-image academy-classicWhyStudy-image--bottom"><img src={classicWhyImageSource2} alt={classicWhy.imageAlt2} /></div></Fragment>;
                return <Fragment key={`why-fragment-${index + 1}`}>{card}</Fragment>;
              })}
            </div>
          </section>

          <section id="courses" className="academy-template-section academy-classicCourses">
            <div className="academy-classicCourses-heading">
              <span>{websiteContent.coursesEyebrow}</span>
              <h2>{websiteContent.coursesTitle}</h2>
              <p>{websiteContent.coursesDescription}</p>
            </div>

            {courses.length ? (
              <div className="academy-classicCourseCards academy-course-list">
                {courses.map((course) => (
                  <article className="academy-classicCourseCard" key={course.id}>
                    <div className="academy-classicCourseCard-image">
                      <img src={course.imageURL || classicHeroImageSource} alt="" />
                      <span><Play size={10} fill="currentColor" /> {course.duration || websiteContent.classicCourses.cardBadgeFallback}</span>
                      <Bookmark size={15} aria-hidden="true" />
                    </div>
                    <h3>{course.name}</h3>
                    <p>{course.code || websiteContent.classicCourses.programmeFallback} <b>•</b> {course.duration || websiteContent.classicCourses.durationFallback}</p>
                    <div className="academy-classicCourseCard-footer">
                      <span className="academy-classicCourseCard-rating"><b>{websiteContent.classicCourses.ratingStars}</b> <small>({course.rating || websiteContent.classicCourses.ratingFallback})</small></span>
                      <Button onClick={() => openSignup(course)}>{websiteContent.classicCourses.cardAction}</Button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState title="No public courses yet." message="This academy has not published courses for public signup." icon={BookOpen} />
            )}
          </section>

          <section id="contact" className="academy-template-section academy-classicContact">
            <div className="academy-template-sectionHeading">
              <span>{classicContact.eyebrow}</span>
              <h2>{classicContact.title}</h2>
              <p>{classicContact.description}</p>
            </div>

            <div className="academy-classicContactLayout">
              <div className="academy-contactGrid">
                {classicContactDetails.map((item) => {
                  const Icon = item.icon;
                  return (
                    <article className="academy-contactCard" key={item.label}>
                      <div className="academy-contactIcon"><Icon size={18} /></div>
                      <strong>{item.label}</strong>
                      <span>{item.value}</span>
                      <p>{item.hint}</p>
                    </article>
                  );
                })}
              </div>
              <div className="academy-classicContactCTA">
                <span>{classicContact.ctaEyebrow}</span>
                <h3>{classicContact.ctaTitle}</h3>
                <p>{classicContact.ctaDescription}</p>
                <div className="academy-contactActions">
                  <Button onClick={handleHeroApply} icon={Send}>{classicContact.primaryAction}</Button>
                  <Button to="/login" variant="secondary" icon={GraduationCap}>{classicContact.secondaryAction}</Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : templateVariant === 'minimal' ? (
        <>
          <section
            id="home"
            className="academy-template-hero academy-template-hero--minimal"
            style={{
              '--academy-hero-image': `url(${minimalHeroImageSource})`,
              backgroundPosition: minimalHero.imagePosition,
            }}
          >
            <div className="academy-minimalHeroLayout">
              <div className="academy-minimalHero">
              <p className="academy-kicker">{minimalHero.eyebrow}</p>
              {isPreviewMode ? <p className="academy-previewBadge">Template preview · {templateTitle}</p> : null}
              <h1><span>{minimalHero.title || heroTitle}</span><em>{minimalHero.accent}</em></h1>
              <p className="academy-heroCopy">{minimalHero.description || heroDescription}</p>
              <div className="academy-template-heroActions academy-minimalHeroActions">
                <Button onClick={handleHeroApply}>{minimalHero.primaryAction}</Button>
                <Button onClick={() => scrollToSection('about')} variant="secondary" icon={Play}>{minimalHero.secondaryAction}</Button>
              </div>
              <div className="academy-minimalHeroMeta">
                {minimalAcademyStats.map((item) => (
                  <div className="academy-minimalStat" key={item.label}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                    <small>{item.hint}</small>
                  </div>
                ))}
              </div>
              </div>
              <div className="academy-minimalHeroArt" aria-hidden="true">
                <div className="academy-minimalHeroShape academy-minimalHeroShape--cyan" style={{ backgroundColor: minimalHero.shapeCyan }} />
                <div className="academy-minimalHeroShape academy-minimalHeroShape--purple" style={{ backgroundColor: minimalHero.shapePurple }} />
                <div className="academy-minimalHeroShape academy-minimalHeroShape--yellow" style={{ backgroundColor: minimalHero.shapeYellow }} />
                <div className="academy-minimalHeroShape academy-minimalHeroShape--red" style={{ backgroundColor: minimalHero.shapeRed }} />
                <div className="academy-minimalHeroPortrait academy-minimalHeroPortrait--main"><img src={minimalHeroImageSource} alt={minimalHero.imageAlt} style={{ objectPosition: minimalHero.imagePosition }} /></div>
                <div className="academy-minimalHeroPortrait academy-minimalHeroPortrait--top"><img src={minimalHeroImageSource2} alt={minimalHero.imageAlt2} style={{ objectPosition: minimalHero.imagePosition }} /></div>
                <div className="academy-minimalHeroPortrait academy-minimalHeroPortrait--bottom"><img src={minimalHeroImageSource3} alt={minimalHero.imageAlt3} style={{ objectPosition: minimalHero.imagePosition }} /></div>
              </div>
            </div>
          </section>

          <section className="academy-minimalBenefits" aria-label="Why learn with us">
            {minimalBenefits.cards.map((item, index) => { const BenefitIcon = [Users, BadgeCheck, Play][index]; const tone = ['orange', 'cyan', 'red'][index]; return <div className="academy-minimalBenefit" key={`benefit-${index + 1}`}><div className={`academy-minimalBenefit-icon academy-minimalBenefit-icon--${tone}`} style={{ backgroundColor: item.iconBackground, color: item.iconColor }}><BenefitIcon size={21} fill={index === 2 ? 'currentColor' : 'none'} /></div><div><h3>{item.title}</h3><p>{item.description}</p></div></div>; })}
          </section>

          <section id="about" className="academy-template-section academy-minimalSection">
            <div className="academy-minimalAboutReference">
              <div className="academy-minimalAboutReference-image">
                <img src={minimalAboutImageSource} alt={minimalAbout.imageAlt} style={{ objectPosition: minimalAbout.imagePosition }} />
                <span><strong>{courses.length || 0}+</strong> {minimalAbout.learningPathsLabel}</span>
              </div>
              <div className="academy-minimalAboutReference-copy">
                <span className="academy-minimalReference-kicker">{minimalAbout.eyebrow}</span>
                <h2>{minimalAbout.title}</h2>
                <p>{minimalAbout.description}</p>
                <div className="academy-minimalAboutReference-list">
                  {minimalAbout.bullets.map((item) => (
                    <div key={item}><BadgeCheck size={17} /><span>{item}</span></div>
                  ))}
                </div>
                <div className="academy-minimalAboutReference-actions">
                  <Button onClick={() => scrollToSection('courses')}>{minimalAbout.primaryAction}</Button>
                  <Button onClick={handleHeroApply} variant="secondary">{minimalAbout.secondaryAction}</Button>
                </div>
              </div>
            </div>
          </section>

          <section id="courses" className="academy-template-section academy-minimalCourses">
            <div className="academy-template-sectionHeading academy-minimalSectionHeading">
              <span>{minimalCourses.eyebrow}</span>
              <h2>{minimalCourses.title}</h2>
              <p>{minimalCourses.description}</p>
            </div>

            {courses.length ? (
              <div className="academy-minimalCourseCards academy-course-list">
                {courses.map((course) => {
                  const matchingIntakes = intakes.filter((intake) => intake.courseId === course.id);
                  return (
                    <article className="academy-minimalCourseCard" key={course.id}>
                      <div className="academy-minimalCourseCard-image">
                        <img src={course.imageURL || heroImageSource} alt={`${course.name} course`} />
                        <span><Play size={10} fill="currentColor" /> {course.duration || minimalCourses.durationFallback}</span>
                        <Bookmark size={15} aria-hidden="true" />
                      </div>
                      <div className="academy-minimalCourseCard-body">
                        <span className="academy-minimalCourseCard-code">{course.code || minimalCourses.codeFallback}</span>
                        <h3>{course.name}</h3>
                        <p>{course.description || course.publicEnrollment?.signupMessage || minimalCourses.descriptionFallback}</p>
                        <div className="academy-minimalCourseCard-meta">
                          <strong>{formatCurrency(course.feeAmount || 0, currency)}</strong>
                          <small>{matchingIntakes.length ? `${matchingIntakes.length} ${matchingIntakes.length === 1 ? minimalCourses.intakeSingular : minimalCourses.intakePlural}` : minimalCourses.openIntakeLabel}</small>
                        </div>
                        <Button onClick={() => openSignup(course)}>{minimalCourses.cardAction}</Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <EmptyState title={minimalCourses.emptyTitle} message={minimalCourses.emptyMessage} icon={BookOpen} />
            )}
          </section>

          <section id="contact" className="academy-template-section academy-minimalContact">
            <div className="academy-minimalContactStage">
              <div className="academy-minimalContactIntro">
                <span>{minimalContact.eyebrow}</span>
                <h2>{minimalContact.title}</h2>
                <p>{minimalContact.description}</p>
                <div className="academy-contactActions academy-minimalContactActions">
                  <Button onClick={handleHeroApply}>{minimalContact.primaryAction}</Button>
                  <Button to="/login" variant="secondary">{minimalContact.secondaryAction}</Button>
                </div>
                <div className="academy-minimalContactOrb" aria-hidden="true"><span>{minimalContact.orbText}</span></div>
              </div>
              <div className="academy-minimalContactDetails">
                <span className="academy-minimalContactDetails-label">{minimalContact.detailsHeading}</span>
                {minimalContactDetails.map((item) => {
                  const Icon = item.icon;
                  return (
                    <article className="academy-minimalContactRow" key={item.label}>
                      <div className="academy-minimalContactRow-icon"><Icon size={17} /></div>
                      <div><strong>{item.label}</strong><span>{item.value}</span><p>{item.hint}</p></div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          <section
            id="home"
            className="academy-template-hero"
            style={{
              '--academy-hero-image': `url(${heroImageSource})`,
              backgroundPosition: websiteContent.heroImagePosition,
            }}
          >
            <div className="academy-modernHero-grid">
              <div className="academy-template-heroInner">
                <p className="academy-kicker">{websiteContent.heroEyebrow}</p>
                {isPreviewMode ? <p className="academy-previewBadge">Template preview · {templateTitle}</p> : null}
                <h1>{heroTitle}</h1>
                <p className="academy-heroCopy">{heroDescription}</p>

                <div className="academy-template-heroActions">
                  <Button onClick={handleHeroApply} icon={Send}>{websiteContent.heroPrimaryAction}</Button>
                  <Button to="/login" variant="secondary" icon={GraduationCap}>{websiteContent.heroSecondaryAction}</Button>
                </div>

                <div className="academy-template-chipRow" aria-label="Academy summary">
                  {modernAcademyStats.map((item) => (
                    <div className="academy-template-chip" key={item.label}>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                      <small>{item.hint}</small>
                    </div>
                  ))}
                </div>

                <p className="academy-template-heroNote">{websiteContent.heroNote || (featuredCourse ? openingNote : modernHero.heroNoteFallback)}</p>
                <div className="academy-classicHero-trust"><span>{websiteContent.heroTrust}</span><i /> <b>{tenant.name}</b></div>
              </div>
              <div className="academy-modernHero-art">
                <img src={heroImageSource} alt={modernHero.imageAlt || `${tenant.name} learning community`} />
                <div className="academy-modernHero-note"><Sparkles size={18} /><span>{modernHero.artNoteLine1}<br />{modernHero.artNoteLine2}<br /><b>{modernHero.artNoteEmphasis}</b></span></div>
                <div className="academy-modernHero-chip"><BookOpen size={18} /><span>{courses.length || modernHero.courseBadgeCountFallback} {modernHero.courseBadgeLabel}<br />{modernHero.courseBadgeSuffix}</span></div>
                <div className="academy-modernHero-status"><i /> {modernHero.admissionsStatus}</div>
              </div>
            </div>
          </section>

          <section id="about" className="academy-template-section academy-aboutSection academy-modernAboutSection">
            <div className="academy-modernAbout-heading">
              <div className="academy-template-sectionHeading">
                  <span>{modernAbout.eyebrow}</span>
                <h2>{modernAbout.title}</h2>
              </div>
              <p>{modernAbout.description}</p>
            </div>

            <div className="academy-modernAbout-story">
              <div className="academy-aboutStory">
                <span className="academy-modernAbout-label">{modernAbout.eyebrow}</span>
                <p>{modernAbout.body}</p>
                <div className="academy-modernAbout-story-image"><img src={modernAboutImageSource} alt={modernAbout.imageAlt || `${tenant.name} learning community`} style={{ objectPosition: modernAbout.imagePosition }} /></div>
                <div className="academy-aboutList">
                  {modernAbout.bullets.map((item) => (
                    <div className="academy-aboutListItem" key={item}>
                      <BadgeCheck size={18} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <aside className="academy-modernAbout-aside">
                <span className="academy-modernAbout-asideNumber">{modernAbout.asideNumber}</span>
                <strong>{modernAbout.asideTitle}</strong>
                <p>{modernAbout.asideDescription}</p>
                <div className="academy-modernAbout-asideLine"><i /><span>{tenant.name}</span></div>
              </aside>
            </div>

            <div className="academy-modernAbout-principles">
              {modernAbout.principles.map((item, index) => { const PrincipleIcon = [Users, CalendarPlus, BookOpen][index]; return <div className="academy-modernAbout-principle" key={`principle-${index + 1}`}><span>{String(index + 1).padStart(2, '0')}</span><PrincipleIcon size={19} /><strong>{item.title}</strong><p>{item.description}</p></div>; })}
            </div>
          </section>

          <section id="courses" className="academy-template-section academy-coursesSection academy-modernCoursesSection">
            <div className="academy-modernCourses-heading">
              <div className="academy-template-sectionHeading">
                  <span>{modernCourses.eyebrow}</span>
                  <h2>{modernCourses.title}</h2>
              </div>
              <div className="academy-modernCourses-intro">
                <strong>{courses.length || modernCourses.emptyCountLabel}</strong>
                <span>{courses.length === 1 ? modernCourses.singleCourseSuffix : modernCourses.multipleCourseSuffix}</span>
                <p>{modernCourses.description}</p>
              </div>
            </div>

            {courses.length ? (
              <div className="academy-template-courseGrid academy-course-list">
                {courses.map((course) => renderCourseCard(course))}
              </div>
            ) : (
              <EmptyState title={modernCourses.emptyTitle} message={modernCourses.emptyMessage} icon={BookOpen} />
            )}
          </section>

          <section id="contact" className="academy-template-section academy-contactSection academy-modernContactSection">
            <div className="academy-modernContact-heading">
              <div className="academy-template-sectionHeading">
                <span>{modernContact.eyebrow}</span>
                <h2>{modernContact.title}</h2>
              </div>
              <p>{modernContact.description}</p>
            </div>

            <div className="academy-contactGrid">
              {modernContactDetails.map((item) => {
                const Icon = item.icon;
                return (
                  <article className="academy-contactCard" key={item.label}>
                    <div className="academy-contactIcon"><Icon size={18} /></div>
                    <strong>{item.label}</strong>
                    <span>{item.value}</span>
                    <p>{item.hint}</p>
                  </article>
                );
              })}
            </div>

            <div className="academy-modernContact-cta">
              <div>
                <span>{modernContact.ctaEyebrow}</span>
                <strong>{modernContact.ctaTitle}</strong>
                <p>{modernContact.ctaDescription}</p>
              </div>
              <div className="academy-contactActions">
                <Button onClick={handleHeroApply} icon={Send}>{modernContact.primaryAction}</Button>
                <Button to="/login" variant="secondary" icon={GraduationCap}>{modernContact.secondaryAction}</Button>
              </div>
            </div>
          </section>
        </>
      )}

      {templateVariant === 'modern' ? (
        <footer className="academy-modernFooter" style={{ backgroundColor: websiteContent.footerBackgroundColor, color: websiteContent.footerTextColor }}>
          <div className="academy-modernFooter-top">
            <div className="academy-modernFooter-intro">
              <span className="academy-modernFooter-kicker"><i /> {footer.eyebrow}</span>
              <h2>{footer.title}</h2>
              <p>{footer.description.replace('your academy', tenant.name)}</p>
              <Button onClick={handleHeroApply}>{footer.action} <ArrowRight size={17} /></Button>
            </div>
            <div className="academy-modernFooter-links">
              <span>{footer.navigationHeading}</span>
              <a href="#home">{footer.homeLabel}</a>
              <a href="#about">{footer.aboutLabel}</a>
              <a href="#courses">{footer.coursesLabel}</a>
              <a href="#contact">{footer.contactLabel}</a>
            </div>
          </div>
          <div className="academy-modernFooter-bottom">
            <span>{tenant.name} · {footer.modernBottomText || academyTypeLabel}</span>
          </div>
        </footer>
      ) : templateVariant === 'classic' ? (
        <footer className="academy-classicFooter" style={{ backgroundColor: websiteContent.footerBackgroundColor, color: websiteContent.footerTextColor }}>
          <div className="academy-classicFooter-top">
            <div className="academy-classicFooter-intro">
              <span>{footer.eyebrow}</span>
              <h2>{footer.title}</h2>
              <p>{footer.description.replace('your academy', tenant.name)}</p>
              <Button onClick={handleHeroApply} icon={ArrowRight}>{footer.action}</Button>
            </div>
            <div className="academy-classicFooter-links">
              <div>
                <span>{footer.navigationHeading}</span>
                <a href="#home">{footer.homeLabel}</a>
                <a href="#about">{footer.aboutLabel}</a>
                <a href="#courses">{footer.coursesLabel}</a>
                <a href="#contact">{footer.contactLabel}</a>
              </div>
              <div>
                <span>{footer.secondaryHeading}</span>
                <a href="#contact">{footer.secondaryEmailText || contactDetails[0].value}</a>
                <a href="#contact">{footer.secondaryPhoneText || contactDetails[1].value}</a>
                <a href="/login">{footer.secondaryLoginLabel}</a>
              </div>
            </div>
          </div>
          <div className="academy-classicFooter-bottom">
            <strong>{tenant.name}</strong>
            <span>{footer.classicBottomText || `${academyTypeLabel} · Every learner matters.`}</span>
          </div>
        </footer>
      ) : templateVariant === 'minimal' ? (
        <footer className="academy-minimalFooter" style={{ backgroundColor: websiteContent.footerBackgroundColor, color: websiteContent.footerTextColor }}>
          <div className="academy-minimalFooter-top">
            <div className="academy-minimalFooter-intro">
              <div className="academy-minimalFooter-brand">
                {tenant.branding?.logoURL ? <img className="academy-minimalFooter-logo" src={tenant.branding.logoURL} alt={`${tenant.name} logo`} style={{ transform: `scale(${logoScale})` }} /> : <><div className="academy-template-mark"><span>{tenant.name.charAt(0).toUpperCase()}</span></div><strong>{tenant.name}</strong></>}
              </div>
              <h2>{footer.title}</h2>
              <p>{footer.description.replace('your academy', tenant.name)}</p>
              <Button onClick={handleHeroApply}>{footer.action} <ArrowRight size={16} /></Button>
            </div>
            <div className="academy-minimalFooter-links">
              <div><span>{footer.navigationHeading}</span><a href="#home">{footer.homeLabel}</a><a href="#about">{footer.aboutLabel}</a><a href="#courses">{footer.coursesLabel}</a><a href="#contact">{footer.contactLabel}</a></div>
              <div><span>{footer.learnerHeading}</span><a href="#courses">{footer.learnerCoursesLabel}</a><a href="/login">{footer.learnerLoginLabel}</a><a href="#contact">{footer.learnerContactLabel}</a></div>
            </div>
          </div>
          <div className="academy-minimalFooter-bottom"><span>{tenant.name} · {footer.minimalMetaText || academyTypeLabel}</span><span>{footer.minimalTagline}</span></div>
        </footer>
      ) : (
        <footer className="academy-template-footer">
          <div>
            <strong>{tenant.name}</strong>
            <p>{footer.portalTypeText || academyTypeLabel}</p>
          </div>
          <span>{footer.poweredByLabel}</span>
        </footer>
      )}

      <Modal
        open={Boolean(selectedCourseId)}
        onClose={closeSignup}
        className="academy-application-modal"
        title={submitted ? 'Application received' : otpStep ? 'Verify your phone' : `Apply to ${activeCourse?.name || 'course'}`}
        description={submitted ? 'The academy team can now see your application.' : otpStep ? 'One quick step before we submit your application.' : 'Fill in your details, then verify your phone to submit your application.'}
      >
        {submitted ? (
          <div className="signup-success">
            <div className="signup-success-icon" aria-hidden="true"><CheckCircle2 size={32} strokeWidth={2.5} /></div>
            <span className="signup-success-eyebrow">Application confirmed</span>
            <h3>You&apos;re on your way.</h3>
            <p className="signup-success-intro">Thanks, {values.fullName}. Your application has been sent to <strong>{tenant.name}</strong>.</p>
            <div className="signup-success-details">
              <div><span>Course</span><strong>{activeCourse?.name || values.courseId}</strong></div>
              <div><span>Next step</span><strong>An admissions assistant will contact you</strong></div>
            </div>
            <p className="signup-success-note">We&apos;ve also sent a confirmation to {values.email}. You can use your new account to check your application anytime.</p>
            <div className="signup-success-actions">
              <Button to="/login" icon={Send}>Go to student login</Button>
              <Button onClick={closeSignup}>Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="modal-form">
            {error ? <div className="form-error">{error}</div> : null}
            {!otpStep ? <div className="academy-application-steps" aria-label="Application steps"><span className="is-active"><b>1</b>Your details</span><i /><span><b>2</b>Verify phone</span></div> : <div className="academy-application-steps" aria-label="Application steps"><span className="is-complete"><b>✓</b>Your details</span><i className="is-complete" /><span className="is-active"><b>2</b>Verify phone</span></div>}
            {!otpStep ? <>
            <FormField label="Full Name" id="signup-full-name">
              <input
                id="signup-full-name"
                required
                value={values.fullName}
                onChange={(event) => setValues({ ...values, fullName: event.target.value })}
                placeholder="Jane Doe"
              />
            </FormField>
            <div className="form-grid">
              <FormField label="Gender" id="signup-gender">
                <select id="signup-gender" value={values.gender} onChange={(event) => setValues({ ...values, gender: event.target.value })}>
                  <option value="">Select gender</option>
                  {GENDER_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </FormField>
              <FormField label="Year of Birth" id="signup-year-of-birth">
                <input
                  id="signup-year-of-birth"
                  type="number"
                  min="1900"
                  max={new Date().getFullYear()}
                  value={values.yearOfBirth}
                  onChange={(event) => setValues({ ...values, yearOfBirth: event.target.value })}
                  placeholder="2002"
                />
              </FormField>
            </div>
            <div className="form-grid">
              <FormField label="Country of Residence" id="signup-country">
                <div className="academy-country-select">
                <select
                  id="signup-country"
                  required
                  value={`${values.phoneCountryCode}|${values.phoneCountry}|${values.countryDialCode}`}
                  onChange={selectCountry}
                >
                  {orderedPhoneCountries.map(([code, name, dialCode]) => <option key={`${code}-${dialCode}`} value={`${code}|${name}|${dialCode}`}>{name}</option>)}
                </select>
                <ChevronDown size={14} aria-hidden="true" />
                </div>
              </FormField>
              <FormField label="Phone Number" id="signup-phone">
                <div className="academy-phone-field"><span>+{values.countryDialCode}</span><input id="signup-phone" required inputMode="tel" value={values.phone} onChange={(event) => setValues({ ...values, phone: event.target.value })} placeholder="712 345 678" /></div>
                <small className="academy-phone-note">You&apos;ll receive an OTP to verify this phone number.</small>
              </FormField>
            </div>
            <div id="academy-phone-recaptcha" />
            <FormField label="Email" id="signup-email">
              <input
                id="signup-email"
                type="email"
                required
                value={values.email}
                onChange={(event) => setValues({ ...values, email: event.target.value })}
              />
            </FormField>
            <div className="form-grid">
              <FormField label="Course" id="signup-course">
                <select
                  id="signup-course"
                  value={values.courseId}
                  onChange={(event) => {
                    const nextCourseId = event.target.value;
                    const selection = createSelectionDefaults(nextCourseId);
                    setSelectedCourseId(nextCourseId);
                    setValues((current) => ({ ...current, ...selection }));
                  }}
                >
                  {courses.map((course) => <option key={course.id} value={course.id}>{course.name}</option>)}
                </select>
              </FormField>
              <FormField label="Preferred Mode of Study" id="signup-study-mode">
                  <select id="signup-study-mode" value={values.studyMode} onChange={(event) => setValues({ ...values, studyMode: event.target.value })}>
                  {getPreferredModeOptions(activeCourse).map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <small>Based on the selected course.</small>
              </FormField>
            </div>
            <FormField label="Intake / Cohort" id="signup-intake">
              <select
                id="signup-intake"
                value={values.intakeId}
                onChange={(event) => {
                  const intakeId = event.target.value;
                  const nextIntake = intakes.find((intake) => intake.id === intakeId);
                  const nextSession = normalizeIntakeSessions(nextIntake?.sessions)[0];
                  setValues((current) => ({
                    ...current,
                    intakeId,
                    intakeSessionLabel: nextSession ? formatIntakeSessionLabel(nextSession) : 'Open session',
                  }));
                }}
              >
                {courseIntakes.length ? courseIntakes.map((intake) => <option key={intake.id} value={intake.id}>{intake.name}</option>) : <option value="open">Open intake</option>}
              </select>
            </FormField>
            <FormField label="Class Session Time" id="signup-session">
              <select
                id="signup-session"
                value={values.intakeSessionLabel}
                onChange={(event) => setValues({ ...values, intakeSessionLabel: event.target.value })}
              >
                {sessionOptions.length
                  ? sessionOptions.map((session) => (
                    <option key={formatIntakeSessionLabel(session)} value={formatIntakeSessionLabel(session)}>
                      {formatIntakeSessionLabel(session)}
                    </option>
                  ))
                  : <option value="Open session">Open session</option>}
              </select>
            </FormField>
            </> : (
              <div className="academy-otp-step">
                <div className="academy-otp-step-icon"><CheckCircle2 size={22} /></div>
                <span className="academy-otp-step-eyebrow">Phone verification</span>
                <h3>Check your phone.</h3>
                <p>We sent a 6-digit OTP to <strong>+{values.countryDialCode} {values.phone}</strong>. Enter it below to submit your application.</p>
                <FormField label="Verification code" id="signup-otp"><input id="signup-otp" required autoFocus inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter the 6-digit OTP" /></FormField>
                {otpNotice ? <small className="academy-otp-notice">{otpNotice}</small> : null}
                <button className="academy-otp-resend" type="button" disabled={saving} onClick={resendPhoneOtp}>Didn&apos;t receive it? <strong>Resend code</strong></button>
              </div>
            )}
            <div className="form-actions">
              <Button type="button" variant="ghost" onClick={closeSignup}>Cancel</Button>
              {otpStep ? <Button type="button" variant="ghost" onClick={returnToApplicationDetails}>Edit details</Button> : null}
              <Button type="submit" loading={saving} disabled={otpStep && otp.length < 6} icon={Send}>{otpStep ? 'Verify & submit' : 'Continue with phone verification'}</Button>
            </div>
              </form>
            )}
      </Modal>
    </main>
  );
}
